import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from "react"
import { GetApp } from "../DemoApp"
import { Block, BlockField, BlockRow } from "../../app/Components/Block"
import { Overlay } from "../../app/Components/Overlay"
import { DemoLocalization, LocalizedMessage, DemoScenesMessages, DemoMessages } from "./LocalizedMessage"

export interface SceneRenderContent {
  [content: string]: (frame: SceneFrame) => ReactNode;
}

export interface SceneAction {
  action: "click" | "hover";
  selector: string;
}

export type SceneAnimation = "scroll-up-down" | "scroll-to-highlight" | "scroll-to-top" | "scroll-to-bottom" | "scroll-to-logrow" | "scroll-to-nextday" | "keep-on-nextday" | "show-action-click" | "show-action-click-once" | "auto-show-overlay" | "scroll-highlight-top" | "scroll-highlight-top-slow" | "show-pointer" | "show-pointer-up" | "fade-out-content" | "fade-in-content";

export interface SceneFrame {
  action?: SceneAction;
  animations?: SceneAnimation[];
  content?: string;
  contentOptions?: { screenshot?: string; theme?: string };
  highlight?: string;
  name: string;
  overlayPosition?: "bottom-center" | "center" | "top-center" | "bottom-right" | "bottom-left" | "center-right" | "top-right" | "hide" | undefined;
}

interface SceneHighlight {
  top: number;
  left: number;
  width: number;
  height: number;
}

type SceneType = "concept" | "quickstart" | "quickstart-voyage";

export interface SceneProps {
  content: SceneRenderContent;
  frames: SceneFrame[];
  scene: SceneType;
  initialFrame?: number;
  onClose?: () => void;
}

const SceneMessageKey = (scene: SceneType): keyof DemoScenesMessages => {
  switch (scene) {
    case "concept":
      return "concept";
    case "quickstart":
      return "quickstart";
    case "quickstart-voyage":
      return "quickstartVoyage";
  }
  return "concept";
};

// Pure scene-player logic, grouped so it can be unit-tested without React or the
// DOM (see Tests/PublicTests/README.md). The Scene component below is a thin view
// that consumes these helpers.
export const SceneData = {
  // Maps a scene id to its key in the localized `demo` messages object.
  messageKey: SceneMessageKey,

  // Resolves the message bundle for a language, falling back to English.
  localization(language: string): DemoMessages {
    return DemoLocalization[language as keyof typeof DemoLocalization] ?? DemoLocalization.en;
  },

  // Frame title: prefer the frame's own `<name>_title` message, else the scene title.
  title(loc: DemoMessages, scene: SceneType, frameName: string): string {
    const sceneMessages = loc.demo[SceneMessageKey(scene)];
    const frameTitle = sceneMessages[frameName + "_title"];
    return frameTitle || sceneMessages["title"];
  },

  // Previous/next button labels, each falling back to the English label.
  uiLabels(loc: DemoMessages): { previous: string; next: string } {
    return {
      previous: loc.ui?.previous ?? DemoLocalization.en.ui.previous,
      next: loc.ui?.next ?? DemoLocalization.en.ui.next,
    };
  },

  // Frame navigation, clamped to the [0, length - 1] range.
  nextIndex(frame: number, length: number): number {
    return Math.min(frame + 1, length - 1);
  },
  previousIndex(frame: number): number {
    return Math.max(frame - 1, 0);
  },

  finalHighlights(frameName: string, highlightBoxes: SceneHighlight[]): SceneHighlight[] {
    if (frameName !== "timelineVoyageClick" || highlightBoxes.length <= 1) {
      return highlightBoxes;
    }

    const minTop = Math.min(...highlightBoxes.map((box) => box.top - box.height / 2));
    const maxBottom = Math.max(...highlightBoxes.map((box) => box.top + box.height / 2));
    const minLeft = Math.min(...highlightBoxes.map((box) => box.left - box.width / 2));
    const maxRight = Math.max(...highlightBoxes.map((box) => box.left + box.width / 2));
    const totalWidth = maxRight - minLeft;
    const totalHeight = maxBottom - minTop;

    return [{
      top: minTop + totalHeight / 2,
      left: minLeft + totalWidth / 2,
      width: totalWidth,
      height: totalHeight,
    }];
  },

  // Resolves a frame's content node. Throws when the frame names a content key
  // that the scene's content registry does not provide; renders the mapped node
  // otherwise (and nothing when the frame has no content).
  frameNode(content: SceneRenderContent, frame: SceneFrame): ReactNode {
    if (frame.content && !content[frame.content]) {
      throw new Error(`Content for frame ${frame.name} not found: ${frame.content}`);
    }
    return frame.content && <div key={frame.content}>{content[frame.content](frame)}</div>;
  },
};

export function Scene({ content, frames, scene, initialFrame, onClose: _onClose }: SceneProps) {
  const [frame, setFrame] = useState<number>(initialFrame ?? 0);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);
  const actionRef = useRef<HTMLDivElement>(null);
  const actionInnerRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<SVGSVGElement>(null);
  const pointerUpRef = useRef<SVGSVGElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [highlights, setHighlights] = useState<SceneHighlight[]>([]);
  const [highlightOpacity, setHighlightOpacity] = useState<number>(0);
  const [highlightTransition, setHighlightTransition] = useState<string>("none");

  const app = GetApp();

  const currentFrame = useMemo(() => {
    return frames[frame];
  }, [frame, frames]);

  useEffect(() => {
    // Don't show highlight border if show-pointer animation is active
    const hasPointerAnimation = currentFrame.animations?.includes("show-pointer") || currentFrame.animations?.includes("show-pointer-up");

    if (currentFrame?.contentOptions?.theme) {
      document.documentElement.setAttribute("data-theme", currentFrame.contentOptions.theme);
    }

    if (currentFrame.highlight && !hasPointerAnimation) {
      // Reset to invisible with no transition
      setHighlightOpacity(0);
      setHighlightTransition("none");
      setHighlights([]);

      const timeouts: NodeJS.Timeout[] = [];

      // Step 1: Calculate and render highlights (invisible, no transition)
      const calculateTimeout = setTimeout(() => {
        const elements = document.querySelectorAll(currentFrame.highlight!);
        if (elements.length > 0) {
          const highlightBoxes = Array.from(elements).map((element) => {
            const rect = element.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            const left = rect.left + window.scrollX;
            const width = rect.width;
            const height = rect.height;

            // Add vertical padding for specific scenes
            const verticalPadding = currentFrame.name === "departing" || currentFrame.name === "manualEntry3" || currentFrame.name === "endDay" || currentFrame.name === "endDay4" ? 10 : 0;

            const centerX = left + width / 2;
            const centerY = top + height / 2;

            return {
              top: centerY,
              left: centerX,
              width: width,
              height: height + verticalPadding * 2,
            };
          });

          const finalHighlights = SceneData.finalHighlights(currentFrame.name, highlightBoxes);

          setHighlights(finalHighlights);

          // Step 2: Add transition after highlights are rendered
          const transitionTimeout = setTimeout(() => {
            setHighlightTransition("opacity 1000ms ease-in-out");

            // Step 3: Change opacity to trigger the transition
            const fadeTimeout = setTimeout(() => {
              setHighlightOpacity(1);
            }, 50); // Small delay to ensure transition is applied first

            timeouts.push(fadeTimeout);
          }, 100); // Wait 100ms before starting fade

          timeouts.push(transitionTimeout);
        } else {
          setHighlights([]);
        }
      }, 100); // Wait for DOM

      timeouts.push(calculateTimeout);

      return () => {
        timeouts.forEach((t) => clearTimeout(t));
      };
    } else {
      setHighlights([]);
      setHighlightOpacity(0);
      setHighlightTransition("none");
      return undefined;
    }
  }, [currentFrame]);

  useEffect(() => {
    if (!currentFrame.animations) {
      setOverlayVisible(true);
      return;
    }
    const hasAutoShow = currentFrame.animations.includes("auto-show-overlay");
    if (hasAutoShow) {
      setOverlayVisible(false);
      const showTimeout = setTimeout(() => {
        setOverlayVisible(true);
      }, 80);
      return () => clearTimeout(showTimeout);
    } else {
      setOverlayVisible(true);
    }
    return;
  }, [currentFrame]);

  useEffect(() => {
    if (!currentFrame.animations) {
      if (pointerRef.current) {
        pointerRef.current.style.display = "none";
      }
      if (pointerUpRef.current) {
        pointerUpRef.current.style.display = "none";
      }
      return;
    }
    const clear: Array<() => void> = [];
    const hasPointer = currentFrame.animations.includes("show-pointer");
    const hasPointerUp = currentFrame.animations.includes("show-pointer-up");
    if (!hasPointer && pointerRef.current) {
      pointerRef.current.style.display = "none";
    }
    if (!hasPointerUp && pointerUpRef.current) {
      pointerUpRef.current.style.display = "none";
    }
    for (const animation of currentFrame.animations) {
      switch (animation) {
        case "scroll-to-highlight":
          if (currentFrame.highlight) {
            const highlightSelector = currentFrame.highlight;
            const timeout = setTimeout(() => {
              const element = document.querySelector(highlightSelector);
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 200); // Wait for DOM to update and render LogView
            clear.push(() => clearTimeout(timeout));
          }
          break;

        case "scroll-highlight-top":
          if (currentFrame.highlight) {
            const element = document.querySelector(currentFrame.highlight!);
            if (element) {
              const rect = element.getBoundingClientRect();
              const absoluteTop = rect.top + window.scrollY;
              // Account for the header offset (104px margin-top on .ShipView)
              const headerOffset = 104;
              window.scrollTo({ top: absoluteTop - headerOffset, behavior: "smooth" });
            }
          }
          break;

        case "scroll-highlight-top-slow":
          if (currentFrame.highlight) {
            const element = document.querySelector(currentFrame.highlight!);
            if (element) {
              const rect = element.getBoundingClientRect();
              const absoluteTop = rect.top + window.scrollY;
              const headerOffset = 104;
              const targetScroll = absoluteTop - headerOffset;
              const startScroll = window.scrollY;
              const distance = targetScroll - startScroll;
              const duration = 2000; // 2 seconds for slower scroll
              const startTime = performance.now();

              const animateScroll = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                // Ease in-out function for smooth animation
                const easeProgress = progress < 0.5
                  ? 2 * progress * progress
                  : 1 - Math.pow(-2 * progress + 2, 2) / 2;

                window.scrollTo(0, startScroll + distance * easeProgress);

                if (progress < 1) {
                  requestAnimationFrame(animateScroll);
                }
              };

              requestAnimationFrame(animateScroll);
            }
          }
          break;

        case "scroll-to-top":
          window.scrollTo({ top: 0, behavior: "smooth" });
          break;

        case "scroll-to-bottom": {
          const bottom = document.documentElement.scrollHeight - window.innerHeight;
          window.scrollTo({ top: bottom, behavior: "smooth" });
          break;
        }

        case "scroll-to-nextday": {
          const nextDayTimeout = setTimeout(() => {
            const dayViews = document.querySelectorAll(".DayView");
            if (dayViews.length > 1) {
              const nextDay = dayViews[1]; // Second DayView is tomorrow
              const rect = nextDay.getBoundingClientRect();
              const absoluteTop = rect.top + window.scrollY;
              const headerOffset = 104;
              const targetScroll = absoluteTop - headerOffset;
              window.scrollTo({ top: targetScroll, behavior: "smooth" });
              // Store the target scroll position immediately
              scrollPositionRef.current = targetScroll;
            }
          }, 300); // Wait for log details to open
          clear.push(() => clearTimeout(nextDayTimeout));
          break;
        }

        case "keep-on-nextday":
        // Restore scroll position after DOM has fully updated
        // Small delay to ensure React has finished rendering new content
        {
          const restoreTimeout = setTimeout(() => {
            if (scrollPositionRef.current > 0) {
              window.scrollTo({ top: scrollPositionRef.current, behavior: "auto" });
            }
          }, 50);
          clear.push(() => clearTimeout(restoreTimeout));
          break;
        }

        case "scroll-to-logrow": {
          const logRow = document.querySelector(".DayView-logs-log");
          if (logRow) {
            const rect = logRow.getBoundingClientRect();
            const absoluteTop = rect.top + window.scrollY;
            const headerOffset = 600; // header minus 2x log entry height to position closer to bottom
            window.scrollTo({ top: absoluteTop - headerOffset, behavior: "smooth" });
          }
          break;
        }

        case "scroll-up-down": {
          const interval = setInterval(() => {
            window.scrollBy(0, Math.sin(Date.now() / 500) * 5);
          }, 20);
          clear.push(() => clearInterval(interval));
          break;
        }
        case "show-action-click":
          if (currentFrame.action) {
          // Hide indicators initially
            if (actionRef.current && actionInnerRef.current) {
              actionRef.current.style.opacity = "0";
              actionInnerRef.current.style.opacity = "0";
            }

            const clickTimeout = setTimeout(() => {
              const element = document.querySelector(currentFrame.action!.selector);
              if (element && actionRef.current && actionInnerRef.current) {
                const rect = element.getBoundingClientRect();
                const centerX = rect.left + window.scrollX + (rect.width / 2);
                const centerY = rect.top + window.scrollY + (rect.height / 2);
                const circleRadius = 16; // Half of 32px circle diameter
                actionRef.current.style.top = `${centerY - circleRadius - 2}px`;
                actionRef.current.style.left = `${centerX - circleRadius - 2}px`;
                actionRef.current.style.opacity = "1";
                actionInnerRef.current.style.top = `${centerY - circleRadius - 2}px`;
                actionInnerRef.current.style.left = `${centerX - circleRadius - 2}px`;
                actionInnerRef.current.style.opacity = "1";
                // Execute the actual click after showing the indicator
                setTimeout(() => {
                  if (element instanceof HTMLElement) {
                    element.click();
                  }
                }, 500); // Click after indicator is visible
              }
            }, 300); // Wait for scroll and DOM updates
            clear.push(() => clearTimeout(clickTimeout));
          }
          break;
        case "show-action-click-once":
          if (currentFrame.action) {
          // Hide indicators initially
            if (actionRef.current && actionInnerRef.current) {
              actionRef.current.style.opacity = "0";
              actionInnerRef.current.style.opacity = "0";
            }

            const clickOnceTimeout = setTimeout(() => {
              const element = document.querySelector(currentFrame.action!.selector);
              if (element && actionRef.current && actionInnerRef.current) {
                const rect = element.getBoundingClientRect();
                const centerX = rect.left + window.scrollX + (rect.width / 2);
                const centerY = rect.top + window.scrollY + (rect.height / 2);
                const circleRadius = 16; // Half of 32px circle diameter
                actionRef.current.style.top = `${centerY - circleRadius - 2}px`;
                actionRef.current.style.left = `${centerX - circleRadius - 2}px`;
                actionRef.current.style.opacity = "1";
                actionInnerRef.current.style.top = `${centerY - circleRadius - 2}px`;
                actionInnerRef.current.style.left = `${centerX - circleRadius - 2}px`;
                actionInnerRef.current.style.opacity = "1";
                // Execute the actual click after showing the indicator
                setTimeout(() => {
                  if (element instanceof HTMLElement) {
                    element.click();
                  }
                  // Fade out the indicator after click
                  setTimeout(() => {
                    if (actionRef.current && actionInnerRef.current) {
                      actionRef.current.style.transition = "opacity 300ms ease-out";
                      actionInnerRef.current.style.transition = "opacity 300ms ease-out";
                      actionRef.current.style.opacity = "0";
                      actionInnerRef.current.style.opacity = "0";
                      // Stop the CSS animation
                      actionInnerRef.current.style.animation = "none";
                    }
                  }, 100); // Small delay after click before fade out
                }, 500); // Click after indicator is visible
              }
            }, 300); // Wait for scroll and DOM updates
            clear.push(() => clearTimeout(clickOnceTimeout));
          }
          break;
        case "show-pointer":
          if (currentFrame.highlight && pointerRef.current) {
            const element = document.querySelector(currentFrame.highlight!);
            if (element) {
              const rect = element.getBoundingClientRect();
              let centerX = rect.left + window.scrollX + (rect.width / 2);
              // Special positioning for endDay scene - move 50% to the left
              if (currentFrame.name === "endDay") {
                centerX = centerX - (rect.width / 3.2);
              }
              const topY = rect.top + window.scrollY;
              pointerRef.current.style.top = `${topY - 140}px`; // 100px line + 40px space
              pointerRef.current.style.left = `${centerX}px`;
              pointerRef.current.style.display = "block";
            }
          }
          break;
        case "show-pointer-up":
          if (currentFrame.highlight && pointerUpRef.current) {
            const element = document.querySelector(currentFrame.highlight!);
            if (element) {
              const rect = element.getBoundingClientRect();
              const centerX = rect.left + window.scrollX + (rect.width / 2);
              const bottomY = rect.bottom + window.scrollY;
              pointerUpRef.current.style.top = `${bottomY}px`;
              pointerUpRef.current.style.left = `${centerX}px`;
              pointerUpRef.current.style.display = "block";
            }
          }
          break;
        case "auto-show-overlay": {
          const timeout = setTimeout(() => {
            nextFrame();
          }, 2000);
          clear.push(() => clearTimeout(timeout));
          break;
        }
        default:
          break;
      }
    }
    if (clear.length > 0) {
      return () => {
        for (const c of clear) {
          c();
        }
      };
    }
    return undefined;
  }, [currentFrame.animations, actionRef?.current, currentFrame.action]);

  const previousFrame = useCallback(() => {
    setFrame(((prev) => SceneData.previousIndex(prev)));
  }, [frame]);

  const nextFrame = useCallback(() => {
    setFrame(((prev) => SceneData.nextIndex(prev, frames.length)));
  }, [frame, frames]);

  const title = useMemo(() => {
    const loc = SceneData.localization(app.language);
    return SceneData.title(loc, scene, currentFrame.name);
  }, [currentFrame, scene, app.language]);

  const uiLabels = useMemo(() => {
    return SceneData.uiLabels(SceneData.localization(app.language));
  }, [app.language]);

  const frameContent = useMemo(() => {
    return SceneData.frameNode(content, currentFrame);
  }, [currentFrame, content]);

  return (
    <div className="Scene" data-scene={scene} data-frame={currentFrame.name}>
      {highlights.map((highlight, index) => (
        <div
          key={`${currentFrame.name}-${index}`}
          className="Scene-highlight"
          style={{
            top: `${highlight.top}px`,
            left: `${highlight.left}px`,
            width: `${highlight.width}px`,
            height: `${highlight.height}px`,
            transform: "translate(-50%, -50%)",
            opacity: highlightOpacity,
            transition: highlightTransition,
          }}
        />
      ))}
      {currentFrame.action && (
        <>
          <div className={`Scene-action-indicator action-${currentFrame.action.action}`} ref={actionRef}></div>
          <div className={`Scene-action-indicator-inner action-${currentFrame.action.action}`} ref={actionInnerRef}></div>
        </>
      )}
      <svg className="Scene-pointer" ref={pointerRef} style={{ display: "none" }} width="32" height="120" viewBox="0 0 32 120">
        <path d="M 16 0 L 16 100 M 6 90 L 16 100 L 26 90" stroke="red" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg className="Scene-pointer-up" ref={pointerUpRef} style={{ display: "none" }} width="32" height="120" viewBox="0 0 32 120">
        <path d="M 16 120 L 16 20 M 6 30 L 16 20 L 26 30" stroke="red" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className={`Scene-content ${currentFrame.animations?.includes("fade-out-content") ? "content-fade-out" : ""}${currentFrame.animations?.includes("fade-in-content") ? " content-fade-in" : ""}`}>
        {frameContent}
      </div>
      <Overlay title={title} hideClose={true} className={`${currentFrame.overlayPosition ? `position-${currentFrame.overlayPosition}` : ""} ${overlayVisible ? "fade-in" : "hidden"}`} containerClassName="Scene-overlay">
        <Block className="Overlay-body">
          <BlockRow>
            <BlockField>
              <LocalizedMessage html={true} message={(m) => m.demo[SceneMessageKey(scene)][currentFrame.name]} />
            </BlockField>
          </BlockRow>
          <BlockRow actions={true}>
            <BlockField><button className="button" onClick={previousFrame} disabled={frame == 0}>{uiLabels.previous}</button></BlockField>
            <BlockField><button className="button" onClick={nextFrame} disabled={frame >= frames.length - 1}>{uiLabels.next}</button></BlockField>
          </BlockRow>
        </Block>
      </Overlay>
    </div>
  );
}
